<template>
  <c-box text-align="center">
    <ul
      v-chakra
      list-style-type="none"
      :mx="{ base: '0', md: 'xl' }"
      :my="{ base: 'md', md: 'xl' }"
      display="inline-block"
    >
      <li v-chakra display="inline-block">
        <c-button
          @click="onClickFirstPage"
          :disabled="isInFirstPage"
          :color="isInFirstPage && 'brand.grayDarkest'"
          :cursor="isInFirstPage && 'not-allowed'"
          py="xs"
          pl="0"
          pr="0"
          border-radius="full"
          border-color="transparent"
          :title="$t('pagination.button.first')"
          :_hover="{ background: 'brand.beige' }"
        >
          <c-icon name="skip_to_first" size="1.25rem" />
        </c-button>
      </li>

      <li v-chakra display="inline-block">
        <c-button
          @click="onClickPreviousPage"
          :disabled="isInFirstPage"
          :color="isInFirstPage && 'brand.grayDarkest'"
          :cursor="isInFirstPage && 'not-allowed'"
          py="xs"
          pl="0"
          pr="0"
          border-radius="full"
          border-color="transparent"
          :title="$t('pagination.button.previous')"
          :_hover="{ background: 'brand.beige' }"
        >
          <c-icon name="chevron_left" size="1.25rem" />
        </c-button>
      </li>

      <li v-for="page in pages" :key="page.name" v-chakra display="inline-block">
        <c-button
          :disabled="page.isDisabled"
          @click="onClickPage(page.name)"
          :color="isPageActive(page.name) && 'white'"
          :background="isPageActive(page.name) && 'black'"
          :opacity="isPageActive(page.name) && '1 !important'"
          p="xs"
          border-color="transparent"
          :_hover="{ background: 'brand.beige' }"
        >
          {{ page.name }}
        </c-button>
      </li>

      <li v-chakra display="inline-block">
        <c-button
          @click="onClickNextPage"
          :disabled="isInLastPage"
          :color="isInLastPage && 'brand.grayDarkest'"
          :cursor="isInLastPage && 'not-allowed'"
          py="xs"
          pl="0"
          pr="0"
          border-radius="full"
          border-color="transparent"
          :title="$t('pagination.button.next')"
          :_hover="{ background: 'brand.beige' }"
        >
          <c-icon name="chevron_right" size="1.25rem" />
        </c-button>
      </li>

      <li v-chakra display="inline-block">
        <c-button
          @click="onClickLastPage"
          :disabled="isInLastPage"
          :color="isInLastPage && 'brand.grayDarkest'"
          :cursor="isInLastPage && 'not-allowed'"
          py="xs"
          pl="0"
          pr="0"
          border-radius="full"
          border-color="transparent"
          :title="$t('pagination.button.last')"
          :_hover="{ background: 'brand.beige' }"
        >
          <c-icon name="skip_to_last" size="1.25rem" />
        </c-button>
      </li>
    </ul>
  </c-box>
</template>

<script lang="ts">
import { CBox, CButton, CIcon } from "@chakra-ui/vue";
import { defineComponent } from "vue";

interface IPaginationProps {
  currentPage: number;
  totalPages: number;
  maxVisibleButtons: number;
  readonly startPage: number;
}

interface IPage {
  name: number;
  isDisabled: boolean;
}

export default defineComponent({
  name: "Pagination",
  components: {
    CBox,
    CButton,
    CIcon,
  },
  props: {
    maxVisibleButtons: {
      type: Number,
      required: false,
      default: 3,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
  },
  computed: {
    startPage(): number {
      const { currentPage, totalPages, maxVisibleButtons } = (this as unknown) as IPaginationProps;

      // First page
      if (currentPage === 1) {
        return 1;
      }

      // Last page
      if (currentPage === totalPages) {
        if (maxVisibleButtons > totalPages) {
          return 1;
        }
        return totalPages - maxVisibleButtons + 1;
      }

      return currentPage - 1;
    },
    pages(): IPage[] {
      const {
        currentPage,
        totalPages,
        maxVisibleButtons,
        startPage,
      } = (this as unknown) as IPaginationProps;
      const range: IPage[] = [];
      const visibleEnd = Math.min(startPage + maxVisibleButtons - 1, totalPages);

      for (let i = startPage; i <= visibleEnd; i++) {
        range.push({
          name: i,
          isDisabled: i === currentPage,
        });
      }

      return range;
    },
    isInFirstPage(): boolean {
      return this.currentPage === 1;
    },
    isInLastPage(): boolean {
      return this.currentPage === this.totalPages;
    },
  },
  methods: {
    onClickFirstPage() {
      this.$emit("pageChanged", 1);
    },
    onClickPreviousPage() {
      this.$emit("pageChanged", this.currentPage - 1);
    },
    onClickPage(page: number) {
      this.$emit("pageChanged", page);
    },
    onClickNextPage() {
      this.$emit("pageChanged", this.currentPage + 1);
    },
    onClickLastPage() {
      this.$emit("pageChanged", this.totalPages);
    },
    isPageActive(page: number): boolean {
      return this.currentPage === page;
    },
  },
});
</script>
